/**
 * Recommendation Utilities for PeopleTag
 * Smart profile recommendation system with multi-factor analysis and explanations
 */

import { calculateMatchScore, calculateDistance, calculateAge } from './profileUtils';

/**
 * Calculate tag similarity score between two profiles
 * @param {Object} profile1 - First profile
 * @param {Object} profile2 - Second profile
 * @returns {number} Tag similarity score (0-100)
 */
const calculateTagSimilarity = (profile1, profile2) => {
  return calculateMatchScore(profile1, profile2);
};

/**
 * Calculate music similarity score between two profiles
 * @param {Object} profile1 - First profile
 * @param {Object} profile2 - Second profile
 * @returns {number} Music similarity score (0-100)
 */
const calculateMusicSimilarity = (profile1, profile2) => {
  const music1 = (profile1.musicList || []).map(song => song.toLowerCase().trim());
  const music2 = (profile2.musicList || []).map(song => song.toLowerCase().trim());

  if (music1.length === 0 || music2.length === 0) return 0;

  const music1Set = new Set(music1);
  const music2Set = new Set(music2);
  
  // Find common songs
  const commonSongs = new Set([...music1].filter(song => music2Set.has(song)));
  
  // Calculate similarity as percentage of common songs out of total unique songs
  const totalUniqueSongs = new Set([...music1, ...music2]).size;
  if (totalUniqueSongs === 0) return 0;
  
  const similarity = (commonSongs.size / totalUniqueSongs) * 100;
  return Math.round(similarity);
};

/**
 * Calculate location proximity score
 * @param {Object} profile1 - First profile with location
 * @param {Object} profile2 - Second profile with location
 * @param {number} maxDistance - Maximum distance in km for full score
 * @returns {number} Location score (0-100)
 */
const calculateLocationScore = (profile1, profile2, maxDistance = 100) => {
  if (!profile1.location || !profile2.location) return 0;
  if (!profile1.location.lat || !profile1.location.lng || 
      !profile2.location.lat || !profile2.location.lng) return 0;

  const distance = calculateDistance(profile1.location, profile2.location, 'km');
  if (distance === null) return 0;

  // Score decreases linearly with distance
  // 0km = 100, maxDistance km = 0
  const score = Math.max(0, 100 - (distance / maxDistance) * 100);
  return Math.round(score);
};

/**
 * Calculate age compatibility score
 * @param {Object} profile1 - First profile
 * @param {Object} profile2 - Second profile
 * @param {number} preferredAgeDiff - Preferred age difference (default 5 years)
 * @returns {number} Age compatibility score (0-100)
 */
const calculateAgeScore = (profile1, profile2, preferredAgeDiff = 5) => {
  const age1 = calculateAge(profile1.birthDate);
  const age2 = calculateAge(profile2.birthDate);

  if (age1 === null || age2 === null) return 0;

  const ageDiff = Math.abs(age1 - age2);
  
  // Score decreases as age difference increases
  // Same age = 100, preferredAgeDiff = 80, 2*preferredAgeDiff = 40, etc.
  const score = Math.max(0, 100 - (ageDiff / preferredAgeDiff) * 20);
  return Math.round(score);
};

/**
 * Calculate bio similarity score using percentage of matching words
 * @param {Object} profile1 - First profile
 * @param {Object} profile2 - Second profile
 * @returns {number} Bio similarity score (0-100)
 */
const calculateBioSimilarity = (profile1, profile2) => {
  const bio1 = (profile1.bio || '').trim();
  const bio2 = (profile2.bio || '').trim();

  if (!bio1 || !bio2) return 0;

  // Extract words (normalize and filter out very short words and common stop words)
  const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'can', 'this', 'that', 'these', 'those', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them', 'am', 'my', 'your', 'his', 'her', 'its', 'our', 'their']);
  
  // Split by whitespace and punctuation, normalize to lowercase, remove empty strings
  const normalizeWords = (text) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ') // Replace punctuation with spaces
      .split(/\s+/)
      .filter(word => word.length > 1 && !stopWords.has(word)); // Keep words longer than 1 char (more lenient)
  };

  const words1 = normalizeWords(bio1);
  const words2 = normalizeWords(bio2);

  if (words1.length === 0 || words2.length === 0) return 0;

  // Create sets of unique words
  const set1 = new Set(words1);
  const set2 = new Set(words2);

  // Find common words
  const commonWords = new Set([...set1].filter(word => set2.has(word)));
  
  // Calculate percentage: (common words) / (total unique words across both bios)
  const totalUniqueWords = new Set([...set1, ...set2]).size;
  
  if (totalUniqueWords === 0) return 0;

  // Percentage of matching words out of total unique words
  const similarity = (commonWords.size / totalUniqueWords) * 100;
  return Math.round(similarity);
};

/**
 * Calculate social media overlap score
 * @param {Object} profile1 - First profile
 * @param {Object} profile2 - Second profile
 * @returns {number} Social overlap score (0-100)
 */
const calculateSocialOverlap = (profile1, profile2) => {
  const socials1 = (profile1.socialLinks || []).filter(s => s && s.trim() !== '');
  const socials2 = (profile2.socialLinks || []).filter(s => s && s.trim() !== '');

  if (socials1.length === 0 || socials2.length === 0) return 0;

  // Extract platform names from URLs
  const extractPlatform = (url) => {
    try {
      const domain = new URL(url).hostname.toLowerCase();
      if (domain.includes('twitter') || domain.includes('x.com')) return 'twitter';
      if (domain.includes('github')) return 'github';
      if (domain.includes('linkedin')) return 'linkedin';
      if (domain.includes('instagram')) return 'instagram';
      if (domain.includes('facebook')) return 'facebook';
      if (domain.includes('youtube')) return 'youtube';
      return domain;
    } catch {
      return null;
    }
  };

  const platforms1 = new Set(socials1.map(extractPlatform).filter(p => p));
  const platforms2 = new Set(socials2.map(extractPlatform).filter(p => p));

  if (platforms1.size === 0 || platforms2.size === 0) return 0;

  const intersection = new Set([...platforms1].filter(p => platforms2.has(p)));
  const union = new Set([...platforms1, ...platforms2]);

  const similarity = intersection.size / union.size;
  return Math.round(similarity * 100);
};

/**
 * Calculate personal data compatibility score
 * @param {Object} profile1 - First profile
 * @param {Object} profile2 - Second profile
 * @returns {Object} Personal data scores for each field
 */
const calculatePersonalDataScore = (profile1, profile2) => {
  const scores = {
    preferredContact: 0,
    relationship: 0,
    lookingFor: 0,
    languages: 0,
    education: 0,
    occupation: 0,
    jobTitle: 0
  };

  // Preferred Contact Method - exact match
  if (profile1.preferredContactMethod && profile2.preferredContactMethod) {
    if (profile1.preferredContactMethod.toLowerCase() === profile2.preferredContactMethod.toLowerCase()) {
      scores.preferredContact = 100;
    }
  }

  // Relationship Status - exact match
  if (profile1.relationshipStatus && profile2.relationshipStatus) {
    if (profile1.relationshipStatus.toLowerCase() === profile2.relationshipStatus.toLowerCase()) {
      scores.relationship = 100;
    }
  }

  // Looking For - exact match
  if (profile1.lookingFor && profile2.lookingFor) {
    if (profile1.lookingFor.toLowerCase() === profile2.lookingFor.toLowerCase()) {
      scores.lookingFor = 100;
    }
  }

  // Languages - similarity based on common languages
  if (profile1.languages && profile2.languages) {
    const lang1 = (profile1.languages || '').toLowerCase();
    const lang2 = (profile2.languages || '').toLowerCase();
    
    if (lang1 && lang2) {
      // Split by comma and normalize
      const langs1 = lang1.split(',').map(l => l.trim()).filter(l => l);
      const langs2 = lang2.split(',').map(l => l.trim()).filter(l => l);
      
      if (langs1.length > 0 && langs2.length > 0) {
        const set1 = new Set(langs1);
        const set2 = new Set(langs2);
        const intersection = new Set([...set1].filter(l => set2.has(l)));
        const union = new Set([...set1, ...set2]);
        
        if (union.size > 0) {
          scores.languages = Math.round((intersection.size / union.size) * 100);
        }
      }
    }
  }

  // Education - exact match
  if (profile1.education && profile2.education) {
    if (profile1.education.toLowerCase() === profile2.education.toLowerCase()) {
      scores.education = 100;
    }
  }

  // Occupation - exact match
  if (profile1.occupation && profile2.occupation) {
    if (profile1.occupation.toLowerCase() === profile2.occupation.toLowerCase()) {
      scores.occupation = 100;
    }
  }

  // Job Title (role) - exact match
  if (profile1.role && profile2.role) {
    if (profile1.role.toLowerCase() === profile2.role.toLowerCase()) {
      scores.jobTitle = 100;
    }
  }

  return scores;
};

/**
 * Calculate overall personal data compatibility score
 * @param {Object} personalScores - Personal data scores object
 * @returns {number} Overall personal data score (0-100)
 */
const calculateOverallPersonalDataScore = (personalScores) => {
  const fields = Object.values(personalScores);
  const nonZeroFields = fields.filter(score => score > 0);
  
  if (nonZeroFields.length === 0) return 0;
  
  // Average of all non-zero scores
  const sum = nonZeroFields.reduce((acc, score) => acc + score, 0);
  return Math.round(sum / nonZeroFields.length);
};

/**
 * Generate explanation for why a profile was recommended
 * @param {Object} scores - Score breakdown object
 * @param {Object} profile - Recommended profile
 * @param {Object} userProfile - User's profile
 * @returns {string} Explanation text
 */
const generateExplanation = (scores, profile, userProfile) => {
  const reasons = [];

  // Tag similarity - show if there are any matching tags
  if (scores.tagScore > 0) {
    const userTags = userProfile.tagsList || [];
    const profileTags = profile.tagsList || [];
    const commonTags = userTags.filter(t => 
      profileTags.some(pt => pt.toLowerCase() === t.toLowerCase())
    );
    if (commonTags.length > 0) {
      // Show tag matches if score > 0 (works for both criteria-specific and general searches)
      reasons.push(`Shares ${commonTags.length} tag${commonTags.length > 1 ? 's' : ''}: ${commonTags.slice(0, 3).join(', ')}${commonTags.length > 3 ? '...' : ''}`);
    }
  }

  // Music similarity - show if there are any matching songs
  if (scores.musicScore > 0) {
    const userMusic = (userProfile.musicList || []).map(song => song.toLowerCase().trim());
    const profileMusic = (profile.musicList || []).map(song => song.toLowerCase().trim());
    const commonSongs = userMusic.filter(song => profileMusic.includes(song));
    if (commonSongs.length > 0) {
      reasons.push(`Shares ${commonSongs.length} song${commonSongs.length > 1 ? 's' : ''}: ${commonSongs.slice(0, 3).join(', ')}${commonSongs.length > 3 ? '...' : ''}`);
    } else {
      // Even if no exact matches, show music similarity percentage for music criteria
      reasons.push(`Music similarity: ${scores.musicScore}%`);
    }
  }

  // Location
  if (scores.locationScore >= 50) {
    const distance = calculateDistance(userProfile.location, profile.location, 'km');
    if (distance !== null) {
      reasons.push(`Located ${Math.round(distance)}km away`);
    } else if (profile.city && userProfile.city && profile.city === userProfile.city) {
      reasons.push(`Same city: ${profile.city}`);
    }
  }

  // Age - show if there's any age compatibility (for criteria-specific) or if score is high (for general)
  if (scores.ageScore > 0) {
    const userAge = calculateAge(userProfile.birthDate);
    const profileAge = calculateAge(profile.birthDate);
    if (userAge !== null && profileAge !== null) {
      const ageDiff = Math.abs(userAge - profileAge);
      if (ageDiff === 0) {
        reasons.push('Same age');
      } else if (ageDiff <= 2) {
        reasons.push(`Similar age (${ageDiff} year${ageDiff > 1 ? 's' : ''} difference)`);
      } else if (ageDiff <= 5) {
        reasons.push(`Age difference: ${ageDiff} year${ageDiff > 1 ? 's' : ''}`);
      } else if (ageDiff <= 10) {
        reasons.push(`Age difference: ${ageDiff} year${ageDiff > 1 ? 's' : ''}`);
      }
    }
  }

  // Bio similarity - show for any bio match when using bio criteria, or for high scores in general
  if (scores.bioScore > 0) {
    if (scores.bioScore >= 40) {
      reasons.push('Similar bio/interests');
    } else {
      reasons.push(`Bio similarity: ${scores.bioScore}%`);
    }
  }

  // Social overlap
  if (scores.socialScore >= 50) {
    reasons.push('Uses similar social platforms');
  }

  // Personal data matches
  if (scores.personalDataScore && scores.personalDataScore >= 50) {
    const personalMatches = [];
    
    if (scores.personalScores?.preferredContact === 100) {
      personalMatches.push('same contact preference');
    }
    if (scores.personalScores?.relationship === 100) {
      personalMatches.push('same relationship status');
    }
    if (scores.personalScores?.lookingFor === 100) {
      personalMatches.push('looking for same');
    }
    if (scores.personalScores?.languages >= 50) {
      personalMatches.push('common languages');
    }
    if (scores.personalScores?.education === 100) {
      personalMatches.push('same education level');
    }
    if (scores.personalScores?.occupation === 100) {
      personalMatches.push('same occupation');
    }
    if (scores.personalScores?.jobTitle === 100) {
      personalMatches.push('similar job title');
    }
    
    if (personalMatches.length > 0) {
      reasons.push(personalMatches.slice(0, 2).join(', '));
    }
  }

  // Profile completeness
  const completeness = (profile.tagsList?.length || 0) + 
                      (profile.socialLinks?.length || 0) + 
                      (profile.bio ? 1 : 0) + 
                      (profile.location ? 1 : 0);
  if (completeness >= 4) {
    reasons.push('Complete profile');
  }

  if (reasons.length === 0) {
    return 'Recommended based on overall compatibility';
  }

  return reasons.join(' • ');
};

/**
 * Get smart profile recommendations
 * @param {Object} userProfile - User's profile
 * @param {Array} allProfiles - Array of all profiles to search
 * @param {Object} options - Recommendation options
 * @returns {Array} Array of recommended profiles with scores and explanations
 */
export const getProfileRecommendations = (userProfile, allProfiles, options = {}) => {
  if (!userProfile || !allProfiles || allProfiles.length === 0) {
    return [];
  }

  const {
    limit = 10,
    minScore = 30,
    weights = {
      tags: 0.30,      // 30% weight on tag similarity
      music: 0.10,     // 10% weight on music similarity
      location: 0.20,  // 20% weight on location
      age: 0.12,       // 12% weight on age compatibility
      bio: 0.08,       // 8% weight on bio similarity
      social: 0.08,    // 8% weight on social overlap
      personal: 0.12   // 12% weight on personal data compatibility
    },
    excludeUsernames = [],
    maxDistance = 100,
    preferredAgeDiff = 5,
    requireLocation = false,
    requireTags = false
  } = options;

  // Normalize weights to sum to 1
  const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
  const normalizedWeights = Object.fromEntries(
    Object.entries(weights).map(([key, value]) => [key, value / totalWeight])
  );

  // Check if this is a criteria-specific search (only one factor matters)
  const isCriteriaSpecific = Object.values(weights).filter(w => w > 0).length === 1;
  const criteriaType = isCriteriaSpecific ? Object.entries(weights).find(([_, w]) => w > 0)?.[0] : null;

  const recommendations = allProfiles
    .filter(profile => {
      // Exclude self
      if (profile.username === userProfile.username) return false;
      
      // Exclude specified usernames
      if (excludeUsernames.includes(profile.username)) return false;

      // Filter by requirements
      if (requireLocation && (!profile.location || !profile.location.lat || !profile.location.lng)) {
        return false;
      }

      if (requireTags && (!profile.tagsList || profile.tagsList.length === 0)) {
        return false;
      }

      // For criteria-specific searches, ensure the relevant data exists
      if (isCriteriaSpecific && criteriaType) {
        if (criteriaType === 'tags' && (!userProfile.tagsList || userProfile.tagsList.length === 0 || !profile.tagsList || profile.tagsList.length === 0)) {
          return false;
        }
        if (criteriaType === 'music' && ((!userProfile.musicList || userProfile.musicList.length === 0) || (!profile.musicList || profile.musicList.length === 0))) {
          return false;
        }
        if (criteriaType === 'location' && (!userProfile.location || !profile.location)) {
          return false;
        }
        if (criteriaType === 'age' && (!userProfile.birthDate || !profile.birthDate)) {
          return false;
        }
        if (criteriaType === 'bio' && (!userProfile.bio || !userProfile.bio.trim() || !profile.bio || !profile.bio.trim())) {
          return false;
        }
        if (criteriaType === 'social' && ((!userProfile.socialLinks || userProfile.socialLinks.length === 0) || (!profile.socialLinks || profile.socialLinks.length === 0))) {
          return false;
        }
        if (criteriaType === 'personal') {
          // For personal, check if at least one personal data field exists
          const userHasPersonal = !!(userProfile.preferredContactMethod || userProfile.relationshipStatus || userProfile.lookingFor || userProfile.languages || userProfile.education || userProfile.occupation || userProfile.role);
          const profileHasPersonal = !!(profile.preferredContactMethod || profile.relationshipStatus || profile.lookingFor || profile.languages || profile.education || profile.occupation || profile.role);
          if (!userHasPersonal || !profileHasPersonal) {
            return false;
          }
        }
      }

      return true;
    })
    .map(profile => {
      // Calculate individual scores
      const tagScore = calculateTagSimilarity(userProfile, profile);
      const musicScore = calculateMusicSimilarity(userProfile, profile);
      const locationScore = calculateLocationScore(userProfile, profile, maxDistance);
      const ageScore = calculateAgeScore(userProfile, profile, preferredAgeDiff);
      const bioScore = calculateBioSimilarity(userProfile, profile);
      const socialScore = calculateSocialOverlap(userProfile, profile);
      
      // Calculate personal data scores
      const personalScores = calculatePersonalDataScore(userProfile, profile);
      const personalDataScore = calculateOverallPersonalDataScore(personalScores);

      // Calculate weighted total score
      const totalScore = Math.round(
        tagScore * normalizedWeights.tags +
        musicScore * (normalizedWeights.music || 0) +
        locationScore * normalizedWeights.location +
        ageScore * normalizedWeights.age +
        bioScore * normalizedWeights.bio +
        socialScore * normalizedWeights.social +
        personalDataScore * (normalizedWeights.personal || 0)
      );

      return {
        profile,
        score: totalScore,
        scores: {
          tagScore,
          musicScore,
          locationScore,
          ageScore,
          bioScore,
          socialScore,
          personalDataScore,
          personalScores,
          totalScore
        },
        explanation: generateExplanation(
          { 
            tagScore,
            musicScore,
            locationScore, 
            ageScore, 
            bioScore, 
            socialScore, 
            personalDataScore,
            personalScores
          },
          profile,
          userProfile
        )
      };
    })
    .filter(rec => rec.score >= minScore)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  return recommendations;
};

/**
 * Get recommendations by specific criteria
 * @param {Object} userProfile - User's profile
 * @param {Array} allProfiles - Array of all profiles
 * @param {string} criteria - 'tags', 'location', 'age', 'bio', 'social', 'personal', or 'all'
 * @param {Object} options - Additional options
 * @returns {Array} Filtered recommendations
 */
export const getRecommendationsByCriteria = (userProfile, allProfiles, criteria = 'all', options = {}) => {
  const baseWeights = {
    tags: 0.30,
    music: 0.10,
    location: 0.20,
    age: 0.12,
    bio: 0.08,
    social: 0.08,
    personal: 0.12
  };

  let weights = { ...baseWeights };
  // Lower minScore for criteria-specific searches since we're only looking at one factor
  let adjustedMinScore = options.minScore !== undefined ? options.minScore : 30;

  // Adjust weights based on criteria
  switch (criteria) {
    case 'tags':
      weights = { tags: 1.0, music: 0, location: 0, age: 0, bio: 0, social: 0, personal: 0 };
      adjustedMinScore = Math.min(adjustedMinScore, 10); // Lower threshold for tag-only matches
      break;
    case 'music':
      weights = { tags: 0, music: 1.0, location: 0, age: 0, bio: 0, social: 0, personal: 0 };
      adjustedMinScore = Math.min(adjustedMinScore, 10); // Lower threshold for music-only matches
      break;
    case 'location':
      weights = { tags: 0, music: 0, location: 1.0, age: 0, bio: 0, social: 0, personal: 0 };
      adjustedMinScore = Math.min(adjustedMinScore, 10); // Lower threshold for location-only matches
      break;
    case 'age':
      weights = { tags: 0, music: 0, location: 0, age: 1.0, bio: 0, social: 0, personal: 0 };
      adjustedMinScore = Math.min(adjustedMinScore, 10); // Lower threshold for age-only matches
      break;
    case 'bio':
      weights = { tags: 0, music: 0, location: 0, age: 0, bio: 1.0, social: 0, personal: 0 };
      adjustedMinScore = Math.min(adjustedMinScore, 5); // Very low threshold for bio matches (any word overlap counts)
      break;
    case 'social':
      weights = { tags: 0, music: 0, location: 0, age: 0, bio: 0, social: 1.0, personal: 0 };
      adjustedMinScore = Math.min(adjustedMinScore, 10); // Lower threshold for social-only matches
      break;
    case 'personal':
      weights = { tags: 0, music: 0, location: 0, age: 0, bio: 0, social: 0, personal: 1.0 };
      adjustedMinScore = Math.min(adjustedMinScore, 10); // Lower threshold for personal-only matches
      break;
    default:
      // 'all' - use base weights and original minScore
      break;
  }

  return getProfileRecommendations(userProfile, allProfiles, {
    ...options,
    weights,
    minScore: adjustedMinScore
  });
};

/**
 * Get recommendations for specific tags
 * @param {Array} tags - Tags to match
 * @param {Array} allProfiles - Array of all profiles
 * @param {Object} options - Additional options
 * @returns {Array} Profiles matching the tags, sorted by relevance
 */
export const getRecommendationsByTags = (tags, allProfiles, options = {}) => {
  if (!tags || tags.length === 0 || !allProfiles || allProfiles.length === 0) {
    return [];
  }

  const { limit = 10, minMatchCount = 1 } = options;

  const tagSet = new Set(tags.map(t => t.toLowerCase().trim()));

  const recommendations = allProfiles
    .map(profile => {
      const profileTags = (profile.tagsList || []).map(t => t.toLowerCase().trim());
      const matchingTags = profileTags.filter(t => tagSet.has(t));
      const matchCount = matchingTags.length;
      const matchRatio = profileTags.length > 0 ? matchCount / profileTags.length : 0;

      // Score based on number of matches and ratio
      const score = Math.round((matchCount * 30) + (matchRatio * 70));

      return {
        profile,
        score,
        matchCount,
        matchingTags,
        matchRatio
      };
    })
    .filter(rec => rec.matchCount >= minMatchCount)
    .sort((a, b) => {
      // Sort by match count first, then by score
      if (b.matchCount !== a.matchCount) {
        return b.matchCount - a.matchCount;
      }
      return b.score - a.score;
    })
    .slice(0, limit);

  return recommendations;
};

/**
 * Get nearby profiles
 * @param {Object} userProfile - User's profile with location
 * @param {Array} allProfiles - Array of all profiles
 * @param {Object} options - Options (maxDistance in km, limit)
 * @returns {Array} Nearby profiles sorted by distance
 */
export const getNearbyProfiles = (userProfile, allProfiles, options = {}) => {
  if (!userProfile || !userProfile.location || !userProfile.location.lat || !userProfile.location.lng) {
    return [];
  }

  const { maxDistance = 50, limit = 20 } = options;

  const nearby = allProfiles
    .filter(profile => {
      if (profile.username === userProfile.username) return false;
      if (!profile.location || !profile.location.lat || !profile.location.lng) return false;
      return true;
    })
    .map(profile => {
      const distance = calculateDistance(userProfile.location, profile.location, 'km');
      return {
        profile,
        distance,
        score: distance !== null && distance <= maxDistance ? Math.round(100 - (distance / maxDistance) * 100) : 0
      };
    })
    .filter(rec => rec.distance !== null && rec.distance <= maxDistance)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit);

  return nearby;
};

/**
 * Get recommendations summary statistics
 * @param {Array} recommendations - Array of recommendation objects
 * @returns {Object} Summary statistics
 */
export const getRecommendationsSummary = (recommendations) => {
  if (!recommendations || recommendations.length === 0) {
    return {
      count: 0,
      averageScore: 0,
      scoreRange: { min: 0, max: 0 },
      topReasons: []
    };
  }

  const scores = recommendations.map(r => r.score);
  const averageScore = Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length);
  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);

  // Extract common reasons from explanations
  const reasonCounts = {};
  recommendations.forEach(rec => {
    if (rec.explanation) {
      const reasons = rec.explanation.split(' • ');
      reasons.forEach(reason => {
        reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
      });
    }
  });

  const topReasons = Object.entries(reasonCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([reason, count]) => ({ reason, count }));

  return {
    count: recommendations.length,
    averageScore,
    scoreRange: { min: minScore, max: maxScore },
    topReasons
  };
};

